import moment from 'moment';
import { combineReducers } from 'redux';
import { createSelector } from 'reselect';
import { call, put, takeLatest, select } from 'redux-saga/effects';
import { push } from 'react-router-redux';
import message from 'antd/lib/message';
import { t } from '../../../app/i18n';
import authApi, { apiCallIds } from '../api';
import { apiCallIds as allocationApiCallIds } from '../../allocations/api';
import { apiCallIds as loansApiCallIds } from '../../loans/api';
import { hideModal, showModal } from '../../../features/modal/ducks';
import { SUBMIT_ACCOUNT_DOCUMENTS } from '../../../features/documents/ducks';
import { WITHDRAW_FUNDS } from '../../../features/payments/ducks';
import { startApiCall, finishApiCall } from '../../../features/spinner/ducks';
import { accountRoles, contractType, creditorRegistrationActions, debtorRegistrationActions } from '../../../common/enums';
import {
  createActionCreator,
  createApiActionCreators,
  createReducer,
  createActionType,
  REQUEST,
  SUCCESS,
  FAILURE,
} from '../../../common/utils/reduxHelpers';
import { fetchLoansActions } from '../../../features/loans/ducks';
import { createBodyForLoanCreationAttempt } from '../api';

/**
 * ACTION TYPES
 */
export const SIGN_UP_REQUEST = 'auth/SIGN_UP_REQUEST';
export const LOGIN = 'auth/LOGIN';
export const TOKEN_LOGIN = 'auth/TOKEN_LOGIN';
export const RELOGIN = 'auth/RELOGIN';
export const LOGOUT = 'auth/LOGOUT';
export const FORGOTTEN_PASSWORD = 'auth/FORGOTTEN_PASSWORD';
export const RESET_PASSWORD = 'auth/RESET_PASSWORD';
export const ACTIVATE_ACCOUNT = 'auth/ACTIVATE_ACCOUNT';
export const FETCH_ME = 'auth/FETCH_ME';
export const FETCH_UNSIGNED_CONTRACTS = 'auth/FETCH_UNSIGNED_CONTRACTS';
export const FETCH_PENDING_CONTRACTS = 'auth/FETCH_PENDING_CONTRACTS';
export const REMOVE_FROM_UNSIGNED_CONTRACTS = 'auth/REMOVE_FROM_UNSIGNED_CONTRACTS';
export const REMOVE_FROM_PENDING_CONTRACTS = 'auth/REMOVE_FROM_PENDING_CONTRACTS';
export const SIGN_CONTRACT = 'auth/SIGN_CONTRACT';
export const SIGN_CONTRACTS = 'auth/SIGN_CONTRACTS';
export const CONFIRM_CONTRACT = 'auth/CONFIRM_CONTRACT';
export const RESEND_SMS_CODE_CONTRACT = 'auth/RESEND_SMS_CODE_CONTRACT';
export const RESEND_ACTIVATION_EMAIL = 'auth/RESEND_ACTIVATION_EMAIL';
export const UPDATE_ACCOUNT_PROFILE = 'auth/UPDATE_ACCOUNT_PROFILE';
export const CHANGE_PASSWORD = 'auth/CHANGE_PASSWORD';
export const FETCH_INVESTED_AMOUNT = 'auth/FETCH_INVESTED_AMOUNT';
export const CHANGE_NOTIFICATIONS = 'auth/CHANGE_NOTIFICATIONS';
export const FETCH_LOAN_CREATION_ATTEMPT = 'auth/FETCH_LOAN_CREATION_ATTEMPT';
export const TOGGLE_AUTOINVEST = 'auth/TOGGLE_AUTOINVEST';
export const SIGN_AUTOINVEST = 'auth/SIGN_AUTOINVEST';
export const UPDATE_LOAN_CREATION_ATTEMPT = 'auth/UPDATE_LOAN_CREATION_ATTEMPT';
export const FETCH_TRUSTSNAP_TOKEN = 'FETCH_TRUSTSNAP_TOKEN';

/**
 * ACTIONS
 */
export const signUpRequest = createActionCreator(SIGN_UP_REQUEST);
export const loginActions = createApiActionCreators(LOGIN);
export const tokenLogin = createActionCreator(TOKEN_LOGIN);
export const relogin = createActionCreator(RELOGIN);
export const logout = createActionCreator(LOGOUT);
export const forgottenPasswordRequest = createActionCreator(FORGOTTEN_PASSWORD);
export const resetPasswordRequest = createActionCreator(RESET_PASSWORD);
export const activateAccountRequest = createActionCreator(ACTIVATE_ACCOUNT);
export const fetchMeActions = createApiActionCreators(FETCH_ME);
export const fetchUnsignedContractsActions = createApiActionCreators(FETCH_UNSIGNED_CONTRACTS);
export const fetchPendingContractsActions = createApiActionCreators(FETCH_PENDING_CONTRACTS);
export const removeFromUnsignedContracts = createActionCreator(REMOVE_FROM_UNSIGNED_CONTRACTS);
export const removeFromPendingContracts = createActionCreator(REMOVE_FROM_PENDING_CONTRACTS);
export const signContractRequest = createActionCreator(SIGN_CONTRACT);
export const signContractsRequest = createActionCreator(SIGN_CONTRACTS);
export const confirmContractRequest = createActionCreator(CONFIRM_CONTRACT);
export const resendSmsCodeContractRequest = createActionCreator(RESEND_SMS_CODE_CONTRACT);
export const resendActivationEmailRequest = createActionCreator(RESEND_ACTIVATION_EMAIL);
export const updateAccountProfileActions = createApiActionCreators(UPDATE_ACCOUNT_PROFILE);
export const changePasswordRequest = createActionCreator(CHANGE_PASSWORD);
export const fetchInvestedAmountActions = createApiActionCreators(FETCH_INVESTED_AMOUNT);
export const changeNotificationsActions = createApiActionCreators(CHANGE_NOTIFICATIONS);
export const fetchLoanCreationAttemptActions = createApiActionCreators(FETCH_LOAN_CREATION_ATTEMPT);
export const toggleAutoInvestActions = createApiActionCreators(TOGGLE_AUTOINVEST);
export const signAutoInvestActions = createApiActionCreators(SIGN_AUTOINVEST);
export const updateLoanCreationAttemptAction = createApiActionCreators(UPDATE_LOAN_CREATION_ATTEMPT);
export const fetchTrustSnapTokenAction = createApiActionCreators(FETCH_TRUSTSNAP_TOKEN)

/**
 * REDUCERS
 */
const initialState = {
  account: null,
  unsignedContracts: [],
  pendingContracts: [],
  token: null,
  investedAmount: null,
  loanCreationAttempt: null,
  trustSnapToken: null
};

const account = createReducer(initialState.account, {
  [LOGIN]: {
    [SUCCESS]: (state, payload) => payload.account,
    [FAILURE]: (state, payload) => initialState.account,
  },
  [LOGOUT]: state => initialState.account,
  [FETCH_ME]: {
    [SUCCESS]: (state, account) => account,
    [FAILURE]: state => initialState.account,
  },
  [SUBMIT_ACCOUNT_DOCUMENTS]: {
    [SUCCESS]: (state, payload) => payload,
  },
  [UPDATE_ACCOUNT_PROFILE]: {
    [SUCCESS]: (state, payload) => payload,
  },
  [WITHDRAW_FUNDS]: {
    [SUCCESS]: (state, payload) => payload,
  },
  [CHANGE_NOTIFICATIONS]: {
    [SUCCESS]: (state, payload) => {
      const account = state;
      account.profileData.notification = payload;
      return account;
    }
  },
  [TOGGLE_AUTOINVEST]: {
    [SUCCESS]: (state, payload) => {
      const account = state;
      account.autoinvest = payload;
      return account;
    }
  },
  [SIGN_AUTOINVEST]: {
    [SUCCESS]: (state, payload) => {
      const account = state;
      account.autoinvest = payload;
      return account;
    }
  }
});

const unsignedContracts = createReducer(initialState.unsignedContracts, {
  [FETCH_UNSIGNED_CONTRACTS]: {
    [SUCCESS]: (state, unsignedContracts) => unsignedContracts,
    [FAILURE]: state => initialState.unsignedContracts,
  },
  [REMOVE_FROM_UNSIGNED_CONTRACTS]: (state, contractId) => {
    if (Array.isArray(contractId)) {
      return state.filter(item => contractId.every(id => id !== item.id))
    } else {
      return state.filter(item => item.id !== contractId)
    }
  },
  [LOGOUT]: state => initialState.unsignedContracts,
});

const pendingContracts = createReducer(initialState.pendingContracts, {
  [FETCH_PENDING_CONTRACTS]: {
    [SUCCESS]: (state, pendingContracts) => pendingContracts,
    [FAILURE]: state => initialState.pendingContracts,
  },
  [REMOVE_FROM_PENDING_CONTRACTS]: (state, contractId) => {
    if (Array.isArray(contractId)) {
      return state.filter(item => contractId.every(id => id !== item.id))
    } else {
      return state.filter(item => item.id !== contractId)
    }
  },
  [LOGOUT]: state => initialState.pendingContracts,
});

const token = createReducer(initialState.token, {
  [LOGIN]: {
    [SUCCESS]: (state, payload) => payload.token,
    [FAILURE]: (state, payload) => initialState.token,
  },
  [LOGOUT]: state => initialState.token,
});

const trustSnapToken = createReducer(initialState.trustSnapToken, {
  [FETCH_TRUSTSNAP_TOKEN]: {
    [SUCCESS]: (state, payload) => payload.token,
    [FAILURE]: (state, payload) => initialState.token,
  },
});

const investedAmount = createReducer(initialState.investedAmount, {
  [FETCH_INVESTED_AMOUNT]: {
    [SUCCESS]: (state, payload) => payload.investedAmount,
    [FAILURE]: () => null,
  },
});

const loanCreationAttempt = createReducer(initialState.loanCreationAttempt, {
  [FETCH_LOAN_CREATION_ATTEMPT]: {
    [SUCCESS]: (state, payload) => payload,
    [FAILURE]: () => null,
  },
  [UPDATE_LOAN_CREATION_ATTEMPT]: {
    [SUCCESS]: (state, payload) => ({...state, ...payload}),
    [FAILURE]: () => null,
  },
});

export default combineReducers({
  account,
  unsignedContracts,
  pendingContracts,
  token,
  trustSnapToken,
  investedAmount,
  loanCreationAttempt
});

/**
 * SELECTORS
 */
export const selectAuth = state => state.auth;

export const selectAccount = state => selectAuth(state).account;
export const selectUnsignedContracts = state => selectAuth(state).unsignedContracts;
export const selectPendingContracts = state => selectAuth(state).pendingContracts;
export const selectToken = state => selectAuth(state).token;
export const selectTrustSnapToken = state => selectAuth(state).trustSnapToken;

export const selectAccountNumber = state => selectAccount(state).accountNumber;
export const selectAccountId = state => selectAccount(state).id;
export const selectAccountEmail = state => selectAccount(state).email;
export const selectAccountRole = state => selectAccount(state).role;
export const selectAccountRegistrationStatus = state => selectAccount(state).registrationStatus;
export const selectAccountNameFirst = state => selectAccount(state).nameFirst;
export const selectAccountNameLast = state => selectAccount(state).nameLast;
export const selectAccountRegistrationAction = state => selectAccount(state).registrationAction;
export const selectAccountBalance = state => Number(selectAccount(state).balance);
export const selectAccountCreatedAt = state => selectAccount(state).createdAt;
export const selectGeneralContractNumber = state => selectAccount(state).generalContractNumber;
export const selectRegistrationDocumentsRejectReason = state =>
  selectAccount(state).registrationDocumentsRejectReason;
export const selectAccountInvestedAmount = state => selectAuth(state).investedAmount;
export const selectLoanCreationAttempt = state => selectAuth(state).loanCreationAttempt;
export const selectNotifications = state => selectAccount(state).profileData.notification;
export const selectAutoInvest = state => selectAccount(state).autoinvest;

const getContractByType = (contracts, type) => contracts.find(c => c.type === type);

export const selectNextUnsignedContract = createSelector(
  selectUnsignedContracts,
  unsignedContracts =>
    getContractByType(unsignedContracts, contractType.GENERAL) ||
    getContractByType(unsignedContracts, contractType.MANDATE)
);

export const selectUnsignedInsuranceContract = createSelector(
  selectUnsignedContracts,
  unsignedContracts => getContractByType(unsignedContracts, contractType.INSURANCE_APPLICATION)
);

export const selectPendingInsuranceContract = createSelector(
  selectPendingContracts,
  pendingContracts => getContractByType(pendingContracts, contractType.INSURANCE_APPLICATION)
);

export const selectAccountInterests = state => {
  return [
    {
      termMonthsMin: 0,
      termMonthsMax: 6,
      allocationInterestMax: selectAccount(state).interest0_6
    },
    {
      termMonthsMin: 6,
      termMonthsMax: 12,
      allocationInterestMax: selectAccount(state).interest6_12
    },
    {
      termMonthsMin: 12,
      termMonthsMax: 24,
      allocationInterestMax: selectAccount(state).interest12_24
    },
    {
      termMonthsMin: 24,
      termMonthsMax: 36,
      allocationInterestMax: selectAccount(state).interest24_36
    },
    {
      termMonthsMin: 36,
      termMonthsMax: 48,
      allocationInterestMax: selectAccount(state).interest36_48
    },
    {
      termMonthsMin: 48,
      termMonthsMax: 60,
      allocationInterestMax: selectAccount(state).interest48_60
    }
  ]
};

export const selectAccountFullName = createSelector(
  selectAccount,
  account => `${account.nameFirst} ${account.nameLast}`
);

export const selectIsLoggedIn = createSelector(selectAccount, account => account !== null);

export const selectIsAdmin = createSelector(
  selectAccount,
  account => !!(account && account.role === accountRoles.ADMINISTRATOR)
);

export const selectIsOperator = createSelector(
  selectAccount,
  account => !!(account && account.role === accountRoles.OPERATOR)
);

export const selectIsCallCentrum = createSelector(
  selectAccount,
  account => !!(account && account.role === accountRoles.CALL_CENTRUM)
);

export const selectIsCreditor = createSelector(
  selectAccount,
  account => !!(account && account.role === accountRoles.CREDITOR)
);

// Test Tools visibility: every staff role on the admin port plus creditors on
// the client port. Debtors and public visitors never see them.
export const selectCanUseTestTools = createSelector(
  selectAccount,
  account => !!(account && [
    accountRoles.ADMINISTRATOR,
    accountRoles.OPERATOR,
    accountRoles.CALL_CENTRUM,
    accountRoles.CREDITOR,
  ].includes(account.role))
);

export const selectIsDebtor = createSelector(
  selectAccount,
  account => !!(account && account.role === accountRoles.DEBTOR)
);

export const selectIsExecutor = createSelector(
  selectAccount,
  account => !!(account && account.role === accountRoles.EXECUTOR)
);

/**
 * SAGAS
 */
function* signUp(action) {
  const { type, accountData, paymentTotal } = action.payload;
  const resp = yield call(authApi.signUp, type, accountData);

  if (resp.ok && type === 'debtor') {
    const { loan } = accountData;
    window.dataLayer.push({
      event: 'formsent',
      eventAction: 'submit',
      eventLabel: `pozicka_${loan.goal}`,
      eventValue: `${paymentTotal - loan.amount}`,
    });
  }

  if (!resp.ok) {
    message.error(t('Pri odosielaní žiadosti nastala chyba. Skúste to prosím znova.'), 5);
  }

  yield doLogin(resp);
}

function* login(action) {
  const { email, password, captchaToken } = action.payload;
  const resp = yield call(authApi.login, { email, password, captchaToken });

  yield doLogin(resp);
}

function* doLogin(resp) {
  localStorage.setItem('time', moment().toISOString());
  if (resp.ok) {
    yield put(loginActions.success(resp.data));
  } else {
    yield put(loginActions.failure(resp.error));
  }
}

function* forgottenPassword(action) {
  const { email } = action.payload;
  const resp = yield call(authApi.forgottenPassword, { email });

  if (resp.ok) {
    message.success(t('Odkaz na zmenu hesla bol zaslaný Váš na e-mail'), 5);
    yield put(push('/auth/login'));
  }
}

function* resetPassword(action) {
  const { email, password, token } = action.payload;
  const resp = yield call(authApi.resetPassword, { email, password, token });

  yield doLogin(resp);
}

function* activateAccount(action) {
  const { accountId, activationToken } = action.payload;
  const resp = yield call(authApi.activateAccount, {
    accountId,
    activationToken,
  });

  if (resp.ok) {
    message.success(t('Účet bol úspešne aktivovaný'), 3);
    yield doLogin(resp);
  }

  yield put(push('/'));
}

export function* fetchMe(action) {
  const { accountId } = action.payload;
  const resp = yield call(authApi.fetchMe, accountId);

  if (resp.ok) {
    yield put(fetchMeActions.success(resp.data));
  } else {
    yield put(fetchMeActions.failure(resp.error));
  }

  return resp;
}

export function* fetchTrustSnapToken(action) {
  const { accountId } = action.payload;
  const resp = yield call(authApi.fetchTrustSnapToken, accountId);

  if (resp.ok) {
    yield put(fetchTrustSnapTokenAction.success(resp.data));
  } else {
    yield put(fetchTrustSnapTokenAction.failure(resp.error));
  }

  return resp;
}

function* refetchMe({ payload: auth }) {
  if (auth.account && auth.token) {
    const resp = yield fetchMe({
      payload: {
        accountId: auth.account.id,
      },
    });

    if (resp.ok) {
      yield doLogin({
        ...resp,
        data: {
          account: resp.data,
          token: auth.token,
        },
      });
    } else {
      yield doLogin(resp);
    }
  }
}

function* logoutRedirect({ payload }) {
  if (payload && payload.message) {
    message.warning(payload.message);
  }
  localStorage.removeItem('time')
  yield put(push(`/auth/login${(payload && payload.redirect) ? payload.redirect : ''}`));
}

function* fetchUnsignedContracts(action) {
  const { accountId } = action.payload;
  const resp = yield call(authApi.fetchUnsignedContracts, accountId);

  if (resp.ok) {
    yield put(fetchUnsignedContractsActions.success(resp.data));
  } else {
    yield put(fetchUnsignedContractsActions.failure(resp.error));
  }
}

function* fetchPendingContracts(action) {
  const { accountId } = action.payload;
  const resp = yield call(authApi.fetchPendingContracts, accountId);

  if (resp.ok) {
    yield put(fetchPendingContractsActions.success(resp.data));
  } else {
    yield put(fetchPendingContractsActions.failure(resp.error));
  }
}

function* signContract(action) {
  const { accountId, contractId } = action.payload;
  const resp = yield call(authApi.signContract, accountId, contractId);

  if (resp.ok) {
    yield put(hideModal(apiCallIds.SIGN_CONTRACT));
    yield put(fetchMeActions.success(resp.data));
    yield put(removeFromUnsignedContracts(contractId));
    if (
      resp.data.registrationAction === creditorRegistrationActions.ENTER_SMS_CODE_CONTRACT ||
      resp.data.registrationAction === debtorRegistrationActions.ENTER_SMS_CODE_CONTRACT
    ) {
      yield put(showModal({ id: apiCallIds.CONFIRM_CONTRACT }));
    }
  }
}

function* signContracts(action) {
  const { accountId, contracts, pendingLoanId } = action.payload;
  const resp = yield call(authApi.signContracts, accountId, contracts);

  if (resp.ok) {
    yield put(hideModal(apiCallIds.SIGN_CONTRACT));
    yield put(fetchMeActions.success(resp.data));
    yield put(removeFromUnsignedContracts(contracts));
    yield put(removeFromPendingContracts(contracts));
    yield put(fetchPendingContractsActions.request({ accountId }));
    
    yield put(
      fetchLoansActions.request({
        append: false,
        options: {},
      })
    );

    if (pendingLoanId && resp.data.registrationAction === debtorRegistrationActions.ENTER_SMS_CODE_CONTRACT) {
      yield put(showModal({ id: apiCallIds.CONFIRM_CONTRACT }));
    } else if (pendingLoanId) {
      yield put(showModal({ id: loansApiCallIds.CONFIRM_LOAN, data: pendingLoanId }));
    }
  }
}

function* openNextContract() {
  const unsignedContracts = yield select(selectUnsignedContracts);

  if (unsignedContracts.length === 0) {
    yield put(showModal({ id: apiCallIds.CONFIRM_CONTRACT }));
  }
}

function* confirmContract(action) {
  const { accountId, token, confirmBy } = action.payload;
  const resp = yield call(authApi.confirmContract, accountId, token, confirmBy);

  if (resp.ok) {
    yield put(
      fetchLoansActions.request({
        append: false,
        options: {},
      })
    );
    yield put(hideModal(apiCallIds.CONFIRM_CONTRACT));
    yield put(fetchMeActions.success(resp.data));
  }
}

function* resendSmsCodeContract(action) {
  const { accountId, onSuccess, onError } = action.payload;
  const resp = yield call(authApi.resendSmsCodeContract, accountId);

  if (resp.ok) {
    message.success(t('SMS kód bol preposlaný'), 3);
    if (onSuccess) {
      onSuccess();
    }
  } else {
    if (onError) {
      onError(resp.error);
    }
  }
}

function* resendActivationEmail(action) {
  const { accountId } = action.payload;
  const resp = yield call(authApi.resendActivationEmail, accountId);

  if (resp.ok) {
    message.success(t('Aktivačný e-mail bol preposlaný'), 3);
  }
}

function* updateAccountProfile({ payload }) {
  const response = yield call(authApi.updateAccountProfile, payload.accountId, payload.profileData);

  if (response.ok) {
    yield put(updateAccountProfileActions.success(response.data));

    if (payload.redirect) {
      yield put(push(payload.redirect));
    }
  } else {
    yield put(updateAccountProfileActions.failure(response.error));
  }
}

function* changePassword({ payload }) {
  const { accountId, passwordNew, passwordOld } = payload;
  const response = yield call(authApi.changePassword, accountId, passwordNew, passwordOld);

  if (response.ok) {
    message.success(t('Vaše heslo bolo úspešne zmenené'), 3);
  }
}

function* fetchInvestedAmount({ payload }) {
  const response = yield call(authApi.fetchInvestedAmount, payload.accountId);

  if (response.ok) {
    yield put(fetchInvestedAmountActions.success(response.data));
  } else {
    yield put(fetchInvestedAmountActions.failure(response.error));
  }
}

function* changeAccountNotifications({ payload }) {
  const response = yield call(authApi.changeAccountNotifications, payload);

  if (response.ok) {
    yield put(changeNotificationsActions.success(payload.notifications));
    message.success(t('Vaše nastavenie notifikácií bolo zmenené'), 3);
  } else {
    message.error(response.error);
  }
}

function* fetchLoanCreationAttempt({ payload }) {
  const response = yield call(authApi.fetchLoanCreationAttempt, payload.cookieId);

  if (response.ok) {
    yield put(fetchLoanCreationAttemptActions.success(response.data));
  } else {
    yield put(fetchLoanCreationAttemptActions.failure(response.error));
  }
}

function* toggleAutoInvest({ payload }){
  const response = yield call(authApi.toggleAutoInvest, payload.accountId, payload.data);

  if (response.ok) {
    yield put(toggleAutoInvestActions.success(response.data));
    if (payload.toggle){
      
      yield put(
        showModal({
          id: allocationApiCallIds.SIGN_ALLOCATION,
          data: response.data,
        })
      );
    }
    else {
      message.success(t('Autoinvest bol úspešne vypnutý'), 3);
      yield put(push('/dashboard/overview'))
    }
    
  } else {
    yield put(toggleAutoInvestActions.failure(response.error));
  }
}

function* signAutoInvest({ payload }){
  const response = yield call(authApi.signAutoInvest, payload.accountId)
  if (response.ok){
    yield put(signAutoInvestActions.success(response.data));
    yield put(hideModal(allocationApiCallIds.SIGN_ALLOCATION));
    message.success(t('Autoinvest bol úspešne aktivovaný'), 3);
    yield put(push('/dashboard/overview'))
  }
  else {
    yield put(signAutoInvestActions.failure(response.error));
  }
}

function* updateLoanCreationAttempt({ payload }) {
  let { cookieId, form, step, params } = payload

  let body = createBodyForLoanCreationAttempt(step, form, params)

  const response = yield call(authApi.updateLoanCreationAttempt, cookieId, body);

  if (response.ok) {
    yield put(updateLoanCreationAttemptAction.success(response.data));
  } else {
    yield put(updateLoanCreationAttemptAction.failure(response.error));
  }
}

function* tokenLoginSaga(action) {
  const { token } = action.payload;
  
  yield put(startApiCall({ apiCallId: apiCallIds.TOKEN_LOGIN }));
  
  try {
    yield put(loginActions.success({ token, account: null }));
    
    const resp = yield call(authApi.fetchMeByToken);
    
    if (resp.ok) {
      yield put(loginActions.success(resp.data));
      localStorage.setItem('time', moment().toISOString());
      yield put(push('/dashboard/overview'));
    } else {
      yield put(loginActions.failure(resp.error));
    }
  } finally {
    yield put(finishApiCall({ apiCallId: apiCallIds.TOKEN_LOGIN }));
  }
}

export function* authSaga() {
  yield takeLatest(SIGN_UP_REQUEST, signUp);
  yield takeLatest(createActionType(LOGIN, REQUEST), login);
  yield takeLatest(createActionType(FETCH_ME, REQUEST), fetchMe);
  yield takeLatest(TOKEN_LOGIN, tokenLoginSaga);
  yield takeLatest(FORGOTTEN_PASSWORD, forgottenPassword);
  yield takeLatest(RESET_PASSWORD, resetPassword);
  yield takeLatest(ACTIVATE_ACCOUNT, activateAccount);
  yield takeLatest(RELOGIN, refetchMe);
  yield takeLatest(LOGOUT, logoutRedirect);
  yield takeLatest(createActionType(FETCH_UNSIGNED_CONTRACTS, REQUEST), fetchUnsignedContracts);
  yield takeLatest(createActionType(FETCH_PENDING_CONTRACTS, REQUEST), fetchPendingContracts);
  yield takeLatest(REMOVE_FROM_UNSIGNED_CONTRACTS, openNextContract); // automaticke otvaranie modalu po podpisani zmluvy
  yield takeLatest(SIGN_CONTRACT, signContract);
  yield takeLatest(SIGN_CONTRACTS, signContracts);
  yield takeLatest(CONFIRM_CONTRACT, confirmContract);
  yield takeLatest(RESEND_SMS_CODE_CONTRACT, resendSmsCodeContract);
  yield takeLatest(RESEND_ACTIVATION_EMAIL, resendActivationEmail);
  yield takeLatest(createActionType(UPDATE_ACCOUNT_PROFILE, REQUEST), updateAccountProfile);
  yield takeLatest(CHANGE_PASSWORD, changePassword);
  yield takeLatest(createActionType(FETCH_INVESTED_AMOUNT, REQUEST), fetchInvestedAmount);
  yield takeLatest(createActionType(CHANGE_NOTIFICATIONS, REQUEST), changeAccountNotifications);
  yield takeLatest(createActionType(FETCH_LOAN_CREATION_ATTEMPT, REQUEST), fetchLoanCreationAttempt);
  yield takeLatest(createActionType(TOGGLE_AUTOINVEST, REQUEST), toggleAutoInvest);
  yield takeLatest(createActionType(SIGN_AUTOINVEST, REQUEST), signAutoInvest);
  yield takeLatest(createActionType(UPDATE_LOAN_CREATION_ATTEMPT, REQUEST), updateLoanCreationAttempt);
  yield takeLatest(createActionType(FETCH_TRUSTSNAP_TOKEN, REQUEST), fetchTrustSnapToken);
}



// WEBPACK FOOTER //
// ./src/features/auth/ducks/index.js